import { useState, useEffect, useMemo } from 'react';
import { fetchCollection } from './cms';
import { ROUTE_PAIRS } from './i18n';

// English translations fallback map for standard paths/titles
const EN_TITLE_MAP = {
  '/': 'Home',
  '/produkter': 'Products',
  '/coilanlaeg': 'Coil Processing Lines & Auxiliary Equipment',
  '/coilanlaeg-og-foelgeudstyr': 'Coil Processing Lines & Auxiliary Equipment',
  '/laser': 'Laser',
  '/fiberlaser': 'Fiber Laser',
  '/roerlaser': 'Tube Laser',
  '/slibe-og-afgratemaskiner': 'Grinding & Deburring Machines',
  '/slibemaskiner': 'Grinding Machines',
  '/pladeklipning': 'Shearing & Cutting',
  '/saks': 'Shears (Guillotine Shears)',
  '/panelbukker': 'Panel Benders',
  '/presser': 'Presses',
  '/hydraulisk-presse': 'Hydraulic Presses',
  '/kantpresse': 'Press Brakes',
  '/kantbukker': 'Press Brakes',
  '/kantpresser': 'Press Brakes',
  '/mekanisk-presse': 'Mechanical Presses',
  '/styringer-og-software': 'Controls & Software',
  '/brugte-maskiner': 'Used Machines',
  '/presser-2': 'Used Machines',
  '/reservedele': 'Spare Parts',
  '/udstyr': 'Auxiliary & Coil Equipment',
  '/rettevaerk': 'Straightening Machines',
  '/centralsmoering': 'Central Lubrication',
  '/service': 'Service',
  '/om-os': 'History since 1921',
  '/leverandoerer': 'Suppliers',
  '/pmb-partnere': 'Partners',
  '/samarbejdspartnere': 'Partners',
  '/pmb-job-ledige-stillinger': 'Job Openings',
  '/pmb-salgs-leveringsbetingelser': 'Terms & Conditions',
  '/kontakt': 'Contact',
};

// Hierarchical structured categories matching customer's exact requirement:
// Laser and Presse are NON-CLICKABLE headers grouping their subcategories
export const STRUCTURED_PRODUCT_CATEGORIES = [
  {
    id: 'cat-coilanlaeg',
    type: 'link',
    sti: '/coilanlaeg',
    titel: { da: 'Coilanlæg og følgeudstyr', en: 'Coil Processing Lines & Auxiliary Equipment' },
    slug: 'coilanlaeg',
  },
  {
    id: 'cat-laser-group',
    type: 'group',
    titel: { da: 'Laser', en: 'Laser' },
    isClickable: false,
    items: [
      {
        id: 'cat-fiberlaser',
        sti: '/fiberlaser',
        titel: { da: 'Fiberlaser', en: 'Fiber Laser' },
        slug: 'fiberlaser',
      },
      {
        id: 'cat-roerlaser',
        sti: '/roerlaser',
        titel: { da: 'Rørlaser', en: 'Tube Laser' },
        slug: 'roerlaser',
      },
    ],
  },
  {
    id: 'cat-slibemaskiner',
    type: 'link',
    sti: '/slibe-og-afgratemaskiner',
    titel: { da: 'Slibe- og afgratemaskiner', en: 'Grinding & Deburring Machines' },
    slug: 'slibe-og-afgratemaskiner',
  },
  {
    id: 'cat-pladeklipning',
    type: 'link',
    sti: '/pladeklipning',
    titel: { da: 'Pladeklipning', en: 'Shearing & Cutting' },
    slug: 'pladeklipning',
  },
  {
    id: 'cat-panelbukker',
    type: 'link',
    sti: '/panelbukker',
    titel: { da: 'Panelbukker', en: 'Panel Benders' },
    slug: 'panelbukker',
  },
  {
    id: 'cat-presse-group',
    type: 'group',
    titel: { da: 'Presse', en: 'Presses' },
    isClickable: false,
    items: [
      {
        id: 'cat-hydraulisk-presse',
        sti: '/hydraulisk-presse',
        titel: { da: 'Hydraulisk presse', en: 'Hydraulic Presses' },
        slug: 'hydraulisk-presse',
      },
      {
        id: 'cat-kantpresse',
        sti: '/kantpresse',
        titel: { da: 'Kantpresse', en: 'Press Brakes' },
        slug: 'kantpresse',
      },
      {
        id: 'cat-mekanisk-presse',
        sti: '/mekanisk-presse',
        titel: { da: 'Mekanisk presse', en: 'Mechanical Presses' },
        slug: 'mekanisk-presse',
      },
    ],
  },
  {
    id: 'cat-styringer-software',
    type: 'link',
    sti: '/styringer-og-software',
    titel: { da: 'Styringer og software', en: 'Controls & Software' },
    slug: 'styringer-og-software',
  },
  {
    id: 'cat-brugte-maskiner',
    type: 'bottom-link',
    sti: '/brugte-maskiner',
    titel: { da: 'Brugte maskiner', en: 'Used Machines' },
    slug: 'brugte-maskiner',
  },
  {
    id: 'cat-reservedele',
    type: 'bottom-link',
    sti: '/reservedele',
    titel: { da: 'Reservedele', en: 'Spare Parts' },
    slug: 'reservedele',
  },
];

// Flat list of clickable items for footer and linear navigation
export const DEFAULT_PRODUCT_ITEMS = [
  { id: 'def-coilanlaeg', sti: '/coilanlaeg', titel: 'Coilanlæg og følgeudstyr', sortering: 1 },
  { id: 'def-fiberlaser', sti: '/fiberlaser', titel: 'Fiberlaser', sortering: 2 },
  { id: 'def-roerlaser', sti: '/roerlaser', titel: 'Rørlaser', sortering: 3 },
  { id: 'def-slibemaskiner', sti: '/slibe-og-afgratemaskiner', titel: 'Slibe- og afgratemaskiner', sortering: 4 },
  { id: 'def-pladeklipning', sti: '/pladeklipning', titel: 'Pladeklipning', sortering: 5 },
  { id: 'def-panelbukker', sti: '/panelbukker', titel: 'Panelbukker', sortering: 6 },
  { id: 'def-hydraulisk-presse', sti: '/hydraulisk-presse', titel: 'Hydraulisk presse', sortering: 7 },
  { id: 'def-kantpresse', sti: '/kantpresse', titel: 'Kantpresse', sortering: 8 },
  { id: 'def-mekanisk-presse', sti: '/mekanisk-presse', titel: 'Mekanisk presse', sortering: 9 },
  { id: 'def-styringer-software', sti: '/styringer-og-software', titel: 'Styringer og software', sortering: 10 },
  { id: 'def-brugte', sti: '/brugte-maskiner', titel: 'Brugte maskiner', sortering: 90 },
  { id: 'def-reservedele', sti: '/reservedele', titel: 'Reservedele', sortering: 91 },
];

export const DEFAULT_ABOUT_ITEMS = [
  { id: 'def-om-os', sti: '/om-os', titel: 'Der var engang… (Historie)', sortering: 31 },
  { id: 'def-leverandoerer', sti: '/leverandoerer', titel: 'Leverandører', sortering: 32 },
  { id: 'def-partnere', sti: '/pmb-partnere', titel: 'Samarbejdspartnere', sortering: 33 },
  { id: 'def-jobs', sti: '/pmb-job-ledige-stillinger', titel: 'Ledige stillinger', sortering: 34 },
  { id: 'def-terms', sti: '/pmb-salgs-leveringsbetingelser', titel: 'Salgs- & Leveringsbetingelser', sortering: 35 },
];

/**
 * Resolves localized target URL for a path given language
 */
export function getMenuHref(rawPath, isEn) {
  if (!rawPath) return isEn ? '/en' : '/';
  if (rawPath.startsWith('#') || rawPath.startsWith('http://') || rawPath.startsWith('https://')) {
    return rawPath;
  }
  const clean = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
  if (!isEn) return clean;

  const pair = ROUTE_PAIRS.find((p) => p.da === clean);
  if (pair) return pair.en;

  if (clean === '/produkter') return '/en/products';
  if (clean === '/kantbukker') return '/en/press-brakes';
  if (clean === '/brugte-maskiner') return '/en/used-machines';
  return `/en${clean}`;
}

/**
 * Resolves title for menu item given language
 */
export function getMenuTitle(item, isEn) {
  if (!item) return '';
  if (typeof item.titel === 'object' && item.titel !== null) {
    if (isEn && item.titel.en) return item.titel.en;
    if (item.titel.da) return item.titel.da;
    return item.titel._ || Object.values(item.titel)[0] || '';
  }

  const titleStr = String(item.titel || '');
  if (isEn) {
    if (EN_TITLE_MAP[item.sti]) return EN_TITLE_MAP[item.sti];
    // Special checks
    if (titleStr.toLowerCase().includes('brugte')) return 'Used Machines';
    if (titleStr.toLowerCase().includes('kantpress') || titleStr.toLowerCase().includes('kantbukk')) return 'Press Brakes';
    if (titleStr.toLowerCase().includes('saks')) return 'Shears';
    if (titleStr.toLowerCase().includes('reservedel')) return 'Spare Parts';
    if (titleStr.toLowerCase().includes('følgeudstyr') || titleStr.toLowerCase().includes('udstyr')) return 'Auxiliary Equipment';
    if (titleStr.toLowerCase().includes('partner')) return 'Partners';
  }
  return titleStr;
}

/**
 * Checks if an item is one of the bottom pinned items (Reservedele, Brugte maskiner)
 */
export function isBottomMenuItem(item) {
  if (!item) return false;
  const sti = String(item.sti || '').toLowerCase();
  const titel = String(
    typeof item.titel === 'object' ? item.titel?.da || Object.values(item.titel)[0] || '' : item.titel || ''
  ).toLowerCase();
  return (
    sti.includes('reservedele') ||
    sti.includes('spare-parts') ||
    sti.includes('brugte') ||
    sti.includes('used') ||
    titel.includes('reservedele') ||
    titel.includes('spare parts') ||
    titel.includes('brugte maskiner') ||
    titel.includes('used machines')
  );
}

/**
 * Hook to retrieve navigation items dynamically from the 'menu_punkter' CMS collection
 */
export function useNavigationMenu() {
  const [productItems, setProductItems] = useState(DEFAULT_PRODUCT_ITEMS);
  const [aboutItems, setAboutItems] = useState(DEFAULT_ABOUT_ITEMS);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    fetchCollection('menu_punkter').then((rawItems) => {
      if (!isMounted) return;
      if (Array.isArray(rawItems) && rawItems.length > 0) {
        setAllItems(rawItems);

        // Filter product items:
        // Must belong to 'Produkter' parent / term
        // MUST NOT be 'Centralsmøring' or 'Leverandører'
        const prods = rawItems.filter((i) => {
          const parent = String(i.foraelder || '').trim().toLowerCase();
          const hasTerm = i.terms?.['menu-placering']?.includes('produkter');
          const isProduct = parent === 'produkter' || hasTerm;

          const sti = String(i.sti || '').toLowerCase();
          const titel = String(
            typeof i.titel === 'object' ? i.titel?.da || '' : i.titel || ''
          ).toLowerCase();

          // Exclude centralsmøring and leverandører from product menu
          const isCentralsmoering = sti.includes('centralsmoering') || titel.includes('centralsmøring');
          const isLeverandoer = sti.includes('leverandoer') || titel.includes('leverandør');

          return isProduct && !isCentralsmoering && !isLeverandoer;
        });

        // Filter about items:
        // items with foraelder === 'Om os' or terms 'om-os'
        // plus ensure 'Leverandører' is included
        const abouts = rawItems.filter((i) => {
          const parent = String(i.foraelder || '').trim().toLowerCase();
          const hasTerm = i.terms?.['menu-placering']?.includes('om-os');
          const sti = String(i.sti || '').toLowerCase();
          const isLeverandoer = sti.includes('leverandoer');
          return parent === 'om os' || parent === 'om-os' || hasTerm || isLeverandoer;
        });

        if (prods.length > 0) {
          // Normalize "Kantpresser" title to "Kantbukker"
          const normalizedProds = prods.map((item) => {
            const rawTitel = typeof item.titel === 'object' ? item.titel?.da : item.titel;
            if (String(rawTitel).toLowerCase().includes('kantpresser')) {
              return {
                ...item,
                titel: typeof item.titel === 'object' ? { ...item.titel, da: 'Kantbukker' } : 'Kantbukker',
              };
            }
            return item;
          });

          setProductItems(normalizedProds);
        }

        if (abouts.length > 0) {
          // Normalize "PMB Partnere" to "Samarbejdspartnere"
          const normalizedAbouts = abouts.map((item) => {
            const rawTitel = typeof item.titel === 'object' ? item.titel?.da : item.titel;
            if (String(rawTitel).toLowerCase().includes('pmb partnere')) {
              return {
                ...item,
                titel: typeof item.titel === 'object' ? { ...item.titel, da: 'Samarbejdspartnere' } : 'Samarbejdspartnere',
              };
            }
            return item;
          });

          normalizedAbouts.sort((a, b) => (Number(a.sortering) || 99) - (Number(b.sortering) || 99));
          setAboutItems(normalizedAbouts);
        }
      }
      setLoading(false);
    }).catch(() => {
      if (isMounted) setLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  return { structuredProductMenu: STRUCTURED_PRODUCT_CATEGORIES, productItems, aboutItems, allItems, loading };
}
